<template>
  <div>
    <v-progress-circular
        v-if="isLoading"
        :size="70"
        :width="7"
        color="primary"
        indeterminate
        class="centered loading"
    ></v-progress-circular>

    <div class="mx-auto" style="max-width: 900px">
      <div class="row">
        <div class="col-6">
          <div class="form-group mb-3">
            <BrandDropdown
                v-model="brandSelected"
                @setBrand="setBrand"
                @setBrands="setBrands"
                :required="true"
                :styleObject="brandDropDownStyle"
            ></BrandDropdown>
            <div v-if="error.errorBrand" class="text-error">
              {{ error.errorBrand.message || 'กรุณาเลือก' }}
            </div>
          </div>

          <div class="form-group mb-3">
            <label for="start-date">Response date from</label>
            <date-picker
                id="start-date"
                v-model="startDate"
                format="D MMMM yyyy"
                lang="en"
                :value="startDate"
                @change="handleStartDateChange"
            />
          </div>

          <div class="form-group mb-3">
            <label for="yearDropdown" required>Year</label>
            <multiselect
                id="yearDropdown"
                v-model="yearSelected"
                :options="years"
                :allow-empty="false"
                deselect-label=""
                placeholder="Select Year"
            />
            <div v-if="isSubmit && error.errorYear" class="text-error">
              กรุณาเลือก
            </div>
            <div v-else-if="!isSubmit && error.errorYear" class="text-error">
              {{ error.errorYear.message || 'กรุณาเลือก' }}
            </div>
          </div>
        </div>

        <div class="col-6">
          <div class="form-group mb-3">
            <SurveyDropdown
                :key="brandSelected && brandSelected.id"
                v-model="surveySelected"
                :brandCode="brandSelected ? brandSelected.id : null"
                @setSurvey="setSurvey"
                @setSurveys="setSurveys"
                :required="true"
            ></SurveyDropdown>
            <div v-if="error.errorSurvey" class="text-error">
              {{ error.errorSurvey.message || 'กรุณาเลือก' }}
            </div>
          </div>

          <div class="form-group mb-3">
            <label for="end-date">Response date to</label>
            <date-picker
                id="end-date"
                v-model="endDate"
                format="D MMMM yyyy"
                lang="en"
                :value="endDate"
                :minDate="startDate"
                @change="handleEndDateChange"
            />
          </div>

          <div class="form-group mb-3">
            <label for="monthDropdown" required>Month</label>
            <multiselect
                id="monthDropdown"
                v-model="monthSelected"
                :options="monthsWithDisabled"
                label="text"
                track-by="id"
                :allow-empty="false"
                deselect-label=""
                placeholder="Select Month"
            />
            <div v-if="isSubmit && error.errorMonth" class="text-error">
              กรุณาเลือก
            </div>
            <div v-else-if="!isSubmit && error.errorMonth" class="text-error">
              {{ error.errorMonth.message || 'กรุณาเลือก' }}
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-end mt-3 mb-3">
        <div class="p-2 align-self-center">
          <button
              class="btn btn-outline-secondary"
              style="width: 100%; max-width: 131px"
              @click="resetFilter"
          >
            Reset
          </button>
        </div>
        <div class="p-2 align-self-center">
          <button
              class="btn btn-outline-primary"
              style="width: 100%; max-width: 131px"
              @click="exportSurveyReport"
          >
            Export
          </button>
        </div>
        <div class="p-2 align-self-center">
          <button
              class="btn btn-outline-success"
              style="width: 100%; max-width: 131px"
              @click="searchSurveyReport"
          >
            OK
          </button>
        </div>
      </div>
    </div>

    <div class="mt-3" v-if="isPageReady && listSurveys.length > 0">
      <b-table
          class="table table-responsive table-bordered"
          hover
          responsive
          thead-class="text-center align-middle"
          tbody-tr-class="text-center"
          tbody-td-class="align-middle"
          :fields="fields"
          :items="listSurveys"
          :per-page="perPage"
          :current-page="currentPage"
          :sort-by.sync="sort.key"
          :sort-desc.sync="sort.type === 'desc'"
          @sort-changed="sortCompare"
      >
        <template #table-caption v-if="listSurveys.length === 0">
          <h5 class="text-center pt-10">There is no report</h5>
        </template>
        <template v-for="field in fields" v-slot:[`head(${field.key})`]="data">
          <div
              class="table-header-limit"
              :style="{ width: `${calculateWidth(data.label)}px` }"
          >
            {{ data.label }}
          </div>
        </template>
      </b-table>

      <b-pagination
          v-if="isPageReady"
          :total-rows="totalItems"
          v-model="currentPage"
          :per-page="perPage"
          class="my-0"
          aria-controls="my-table"
          @input="changePage"
      ></b-pagination>
    </div>
    <div class="pt-15" v-if="message">
      <h2 class="text-center">{{ message }}</h2>
    </div>
  </div>
</template>

<script>
import BrandDropdown from "components/Brand/BrandDropdown";
import SurveyDropdown from "components/Survey/SurveyDropdown";
import fields from "@/views/Booking/listBookingFields.json";
import DatePicker from "components/Datepicker/Datepicker.vue";
import ReportService from "services/api/report.service";
import moment from "moment";
import monthYearMixin from "@/utils/monthYearMixin";

export default {
  mixins: [monthYearMixin],
  components: {
    DatePicker,
    BrandDropdown,
    SurveyDropdown,
  },
  data() {
    const { surveyId = null, brandCode = null } = this.$route.query;
    return {
      brandSelected: null,
      brandItems: 0,
      surveySelected: null,
      surveys: [],
      isLoading: false,
      isSubmit: false,
      startDate: null,
      endDate: null,
      yearSelected: moment().format("YYYY"),
      years: [],
      monthSelected: null,
      months: [],
      listSurveys: [],
      fields,
      perPage: 10,
      totalItems: 0,
      currentPage: 1,
      sort: { key: "", type: "asc" },
      isPageReady: false,
      error: {
        errorBrand: null,
        errorSurvey: null,
        errorYear: null,
        errorMonth: null,
      },
      incomingSurveyId: surveyId,
      incomingBrandCode: brandCode,
      message: null,
      brandDropDownStyle: {
        margin: "0.6rem 0",
      },
    };
  },

  mounted() {
    this.setYears();
    this.setMonths();
  },
  created() {
    if (this.incomingBrandCode) {
      this.brandSelected = { id: this.incomingBrandCode };
    }
    if (this.incomingSurveyId) {
      this.surveySelected = { id: this.incomingSurveyId };
    }
  },
  methods: {
    setBrand(brand) {
      this.brandSelected = brand || null;
      if (!brand) this.surveySelected = null;
    },
    setBrands(brands) {
      this.brandItems = brands.length;
      if (this.incomingBrandCode) {
        const found = brands.find(
            (b) => String(b.id) === String(this.incomingBrandCode)
        );
        if (found) this.brandSelected = found;
        this.incomingBrandCode = null;
      } else if (brands.length === 1) {
        this.brandSelected = brands[0];
      }
    },
    setSurvey(survey) {
      this.surveySelected = survey || null;
    },
    setSurveys(surveys) {
      this.surveys = surveys || [];
      if (this.incomingSurveyId && this.surveys.length) {
        const found = this.surveys.find(
            (s) => String(s.id) === String(this.incomingSurveyId)
        );
        if (found) {
          this.surveySelected = found;
          this.incomingSurveyId = null;
        }
      }
    },
    resetFilter() {
      this.brandSelected = null;
      this.surveySelected = null;
      this.resetErrorText();
      this.isSubmit = false;
      this.startDate = null;
      this.endDate = null;
    },

    async triggerSurveyReport(mode = "view") {
      this.message = null;
      const isValid = this.validateFilters();
      if (!isValid) {
        this.isLoading = false;
        return;
      }

      this.isLoading = true;

      const brandCode = this.brandSelected?.id || null;
      const month = this.monthSelected?.id || null;
      const year = this.yearSelected ? Number.parseInt(this.yearSelected, 10) : null;

      const responseDateFrom = this.startDate
          ? moment(this.startDate).format("YYYY-MM-DD")
          : null;
      const responseDateTo = this.endDate
          ? moment(this.endDate).format("YYYY-MM-DD")
          : null;
      const surveyId = this.surveySelected?.id ?? 0;

      const param = { brandCode, month, year, responseDateFrom, responseDateTo };

      try {
        const serviceCall =
            mode === "excel"
                ? ReportService.getSurveyReportExcelAsync
                : ReportService.getSurveyReportAsync;

        const res = await serviceCall(param, surveyId);
        const data = res.data;
        this.processId = data.processId;

        await this.getSurveyReportResult(mode);
      } catch (err) {
        this.isLoading = false;
        const errRes = err.response;
        const errorData = errRes?.data;

        if (errorData?.errors?.length) {
          this.setErrorText(errorData.errors);
        }

        if (errRes?.status !== 400) {
          this.message =
              errRes?.data?.message || "An error occurred, please try again later!";
          this.showDialog = true;
        }
      }
    },

    async searchSurveyReport() {
      return this.triggerSurveyReport("view");
    },
    async exportSurveyReport() {
      return this.triggerSurveyReport("excel");
    },

    async getSurveyReportResult(mode = "view") {
      this.isLoading = true;
      try {
        if (mode === "excel") {
          this.listSurveys = [];
          this.totalItems = 0;
          this.isPageReady = false;
        }

        while (true) {
          const res = await ReportService.getSurveyReportResult({
            process: this.processId,
          });
          const data = res.data;

          if (data.status === "success" && data.errors.length > 0) {
            this.setErrorText(data.errors);
            break;
          }

          if (mode === "excel" && data.status === "success" && data.url) {
            window.open(data.url, "_blank");
            break;
          } else if (
              data.status === "success" &&
              Array.isArray(data.header) &&
              Array.isArray(data.data)
          ) {
            this.resetErrorText();
            this.fields = data.header.map((h, index) => ({
              key: `col${index}`,
              label: (h ?? "").toString(),
            }));

            this.listSurveys = data.data.map((row) => {
              const rowObj = {};
              row.forEach((cell, idx) => {
                let value = (cell ?? "").toString().trim() || "-";
                if (idx === 0 && value !== "-") {
                  const m = moment(value, moment.ISO_8601).locale("th");
                  value = m.format("YYYY-MM-DD HH:mm:ss");
                }
                rowObj[`col${idx}`] = value;
              });
              return rowObj;
            });

            this.totalItems =
                data.totalItems ??
                data.total ??
                data.count ??
                this.listSurveys.length;
            this.isPageReady = this.totalItems > 0;
            break;
          }

          if (data.status === "failed" || data.status === "error") {
            this.message = data.message || "Failed to generate report.";
            this.showDialog = true;
            break;
          }

          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      }
      catch (err) {
        console.error("Error in getSurveyReportResult:", err);
        const errorData = err?.response?.data;
        const errorMessage = errorData.message;
        if(errorMessage != null){
          this.message = errorMessage;
        }
        this.listSurveys = [];
        this.totalItems = 0;
        this.isPageReady = false;
      }
      finally {
        this.isLoading = false;
      }
    },

    changePage() {
      this.searchSurveyReport();
    },
    sortCompare(e) {
      this.sort.key = e.sortBy;
      this.sort.type = e.sortDesc ? "desc" : "asc";
      this.searchSurveyReport();
    },
    handleStartDateChange(newDate) {
      if (!newDate || newDate > this.endDate) {
        this.endDate = null;
      }
      this.startDate = newDate;
    },
    handleEndDateChange(newDate) {
      if (newDate < this.startDate) {
        alert("The end date cannot be before the start date.");
        return;
      }
      this.endDate = newDate;
    },
    setYears() {
      const currentYear = Number.parseInt(moment().format("YYYY"));
      for (let i = currentYear; i >= 1950; i--) {
        this.years.push(i.toString());
      }
    },
    setMonths() {
      moment.months().forEach((month, index) => {
        this.months.push({
          id: index + 1,
          text: month,
        });
      });

      const prevMonthMoment = moment().subtract(1, "month");
      const prevMonthId = prevMonthMoment.month() + 1; // month() 0-based
      const prevYear = prevMonthMoment.year();

      this.yearSelected = prevYear.toString();

      this.monthSelected = this.months.find(m => m.id === prevMonthId);
    },
    calculateWidth(text = "") {
      const hasThai = /[ก-ฮ]/.test(text);
      return text.length * (hasThai ? 5 : 12);
    },

    setErrorText(errors) {
      this.error.errorBrand = errors.find((e) => e.fieldName === "brandCode") || null;
      this.error.errorSurvey = errors.find((e) => e.fieldName === "id") || null;
      this.error.errorYear = errors.find((e) => e.fieldName === "year") || null;
      this.error.errorMonth = errors.find((e) => e.fieldName === "month") || null;
    },
    resetErrorText() {
      this.error = {
        errorBrand: null,
        errorSurvey: null,
        errorYear: null,
        errorMonth: null,
      };
    },

    validateFilters() {
      const hasYear = !!this.yearSelected;
      const hasMonth = !!(this.monthSelected && this.monthSelected.id);

      this.error.errorYear = !hasYear;
      this.error.errorMonth = !hasMonth;

      this.isSubmit = true;

      return hasYear && hasMonth;
    },
  },
  watch: {
    currentPage() {
      this.searchSurveyReport();
    },
    yearSelected(newVal) {
      const y = Number.parseInt(newVal, 10);

      if (y > this.currentYearNum) {
        this.monthSelected = null;
        return;
      }

      if (y === this.currentYearNum) {
        const prevMonth = this.currentMonthNum - 1;

        if (prevMonth >= 1) {
          this.monthSelected = this.months.find(m => m.id === prevMonth) || null;
        } else {
          this.monthSelected = null;
        }
        return;
      }

      if (!this.monthSelected) {
          this.monthSelected = this.months.find(m => m.id === 12) || null;
      }
    },
  },
};
</script>

<style scoped>
.centered {
  position: absolute;
  top: 45%;
  left: 50%;
}

table {
  table-layout: auto;
  border-collapse: collapse;
}

.table-header-limit {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: normal;
  word-break: break-word;
}

th {
  width: auto !important;
}

label[required]::after {
  content: "*";
  color: red;
  margin-left: 5px;
}

.text-error {
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 4px;
}
</style>
